import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import BulkVendorImport from '../../../components/ui/BulkVendorImport';
import { formatPrice } from '../../../utils';
import vendorDataService from '../../../services/vendorDataService';
import adminAnalyticsService from '../../../services/adminAnalyticsService';

const VendorManagementPanel = () => {
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [importedVendors, setImportedVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorManagement = {
    totalVendors: vendors.length,
    activeVendors: vendors.filter(v => v.status === 'active').length,
    pendingApprovals: vendors.filter(v => v.status === 'pending').length,
    suspendedVendors: vendors.filter(v => v.status === 'suspended').length
  };

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    setLoading(true);
    const result = await vendorDataService.getVendors({ limit: 1000 });
    if (result.success) {
      setVendors(result.data.map(v => ({
        id: v.id,
        name: v.business_name,
        email: v.user?.email || '',
        category: v.category,
        city: v.city,
        state: v.state,
        rating: v.average_rating || 0,
        reviewCount: v.total_reviews || 0,
        status: v.is_verified ? 'active' : 'pending',
        isVerified: v.is_verified,
        subscriptionType: v.subscription_type,
        joinDate: v.created_at
      })));
    }
    setLoading(false);
  };

  const handleImportComplete = (newVendors) => {
    setImportedVendors(prev => [...prev, ...newVendors]);
    setVendors(prev => [...prev, ...newVendors]);
  };

  const filteredVendors = vendors?.filter(vendor => {
    const matchesSearch = vendor?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         vendor?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && vendor?.status === 'active') ||
                         (filterStatus === 'pending' && vendor?.status === 'pending') ||
                         (filterStatus === 'suspended' && vendor?.status === 'suspended') ||
                         (filterStatus === 'unclaimed' && vendor?.status === 'unclaimed') ||
                         (filterStatus === 'verified' && vendor?.isVerified);
    return matchesSearch && matchesStatus;
  });

  const handleVendorAction = (vendorId, action) => {
    setVendors(prev => prev?.map(vendor => {
      if (vendor?.id === vendorId) {
        switch (action) {
          case 'approve':
            return { ...vendor, status: 'active', isVerified: true };
          case 'suspend':
            return { ...vendor, status: 'suspended' };
          case 'activate':
            return { ...vendor, status: 'active' };
          case 'verify':
            return { ...vendor, isVerified: true };
          default:
            return vendor;
        }
      }
      return vendor;
    }));
  };

  const handleBulkAction = (action) => {
    if (selectedVendors?.length === 0) return;
    
    setVendors(prev => prev?.map(vendor => {
      if (selectedVendors?.includes(vendor?.id)) {
        switch (action) {
          case 'approve':
            return { ...vendor, status: 'active', isVerified: true };
          case 'suspend':
            return { ...vendor, status: 'suspended' };
          case 'verify':
            return { ...vendor, isVerified: true };
          default:
            return vendor;
        }
      }
      return vendor;
    }));
    setSelectedVendors([]);
  };

  const handleSelectVendor = (vendorId) => {
    setSelectedVendors(prev => 
      prev?.includes(vendorId) 
        ? prev?.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVendors?.length === filteredVendors?.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(filteredVendors?.map(vendor => vendor?.id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          Vendor Management
        </h2>
        <Button variant="primary" iconName="Store">
          Add Vendor
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="border-b border-border-light">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'vendors', label: 'All Vendors' },
              { id: 'pending', label: `Pending (${vendorManagement?.pendingApproval})` },
              { id: 'bulk-import', label: 'Bulk Import' },
              { id: 'analytics', label: 'Analytics' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Vendor Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 text-sm font-medium">Total Vendors</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {vendorManagement?.totalVendors?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="Store" size={24} className="text-blue-600" />
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    +{vendorManagement?.newVendorsThisMonth} this month
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-600 text-sm font-medium">Active Vendors</p>
                      <p className="text-2xl font-bold text-green-900">
                        {vendorManagement?.activeVendors?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    {((vendorManagement?.activeVendors / vendorManagement?.totalVendors) * 100)?.toFixed(1)}% active rate
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-600 text-sm font-medium">Pending Approval</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {vendorManagement?.pendingApproval}
                      </p>
                    </div>
                    <Icon name="Clock" size={24} className="text-yellow-600" />
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">Awaiting review</p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-600 text-sm font-medium">Growth Rate</p>
                      <p className="text-2xl font-bold text-orange-900">
                        +{vendorManagement?.vendorGrowthRate}%
                      </p>
                    </div>
                    <Icon name="TrendingUp" size={24} className="text-orange-600" />
                  </div>
                  <p className="text-xs text-orange-700 mt-2">Monthly growth</p>
                </div>
              </div>

              {/* Category Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-text-primary mb-4">Vendors by Category</h3>
                  <div className="space-y-3">
                    {vendorManagement?.topCategories?.map((category, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-text-primary">{category?.category}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${(category?.count / Math.max(...vendorManagement?.topCategories?.map(c => c?.count))) * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">{category?.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-text-primary mb-4">Subscription Distribution</h3>
                  <div className="space-y-3">
                    {Object.entries(vendorManagement?.subscriptionBreakdown)?.map(([type, count], index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-text-primary capitalize">{type}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-secondary h-2 rounded-full" 
                              style={{ width: `${(count / vendorManagement?.totalVendors) * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bulk-import' && (
            <BulkVendorImport onImportComplete={handleImportComplete} />
          )}

          {(activeTab === 'vendors' || activeTab === 'pending') && (
            <div className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
                    <input
                      type="text"
                      placeholder="Search vendors..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="all">All Vendors</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="unclaimed">Unclaimed</option>
                  <option value="suspended">Suspended</option>
                  <option value="verified">Verified</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedVendors?.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-primary-50 rounded-lg">
                  <span className="text-sm text-primary font-medium">
                    {selectedVendors?.length} vendor(s) selected
                  </span>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('approve')}>
                    Approve
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('verify')}>
                    Verify
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('suspend')}>
                    Suspend
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedVendors([])}>
                    Clear
                  </Button>
                </div>
              )}

              {/* Vendors Table */}
              <div className="bg-white border border-border-light rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface-50">
                    <tr>
                      <th className="p-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedVendors?.length === filteredVendors?.length && filteredVendors?.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-border-light"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Vendor</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Category</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Subscription</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Rating</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Status</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {filteredVendors?.map((vendor) => (
                      <tr key={vendor?.id} className="hover:bg-surface-50">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedVendors?.includes(vendor?.id)}
                            onChange={() => handleSelectVendor(vendor?.id)}
                            className="rounded border-border-light"
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={vendor?.image || '/assets/images/no_image.png'}
                              alt={vendor?.name}
                              className="w-10 h-10 rounded-lg object-cover"
                              loading="lazy"
                            />
                            <div>
                              <p className="font-medium text-text-primary">{vendor?.name}</p>
                              <p className="text-sm text-text-secondary">{vendor?.location}</p>
                              {vendor?.status === 'unclaimed' && (
                                <span className="text-xs text-orange-600 font-medium">
                                  {vendor?.claimStatus || 'Claim Your Business'}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-text-primary">
                          {vendor?.category}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            vendor?.subscriptionType === 'premium' ?'bg-purple-100 text-purple-600' 
                              : vendor?.subscriptionType === 'basic' ?'bg-blue-100 text-blue-600' :'bg-gray-100 text-gray-600'
                          }`}>
                            {vendor?.subscriptionType}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-1">
                            <Icon name="Star" size={14} className="text-yellow-500" fill="currentColor" />
                            <span className="text-sm text-text-primary">{vendor?.rating || 0}</span>
                            <span className="text-xs text-text-secondary">({vendor?.reviewCount || 0})</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              vendor?.status === 'active' ?'bg-green-100 text-green-600' 
                                : vendor?.status === 'pending' ?'bg-yellow-100 text-yellow-600'
                                : vendor?.status === 'unclaimed'?'bg-orange-100 text-orange-600' :'bg-red-100 text-red-600'
                            }`}>
                              {vendor?.status}
                            </span>
                            {vendor?.isVerified && (
                              <Icon name="CheckCircle" size={16} className="text-blue-500" />
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              onClick={() => console.log('View vendor:', vendor?.id)}
                            />
                            {vendor?.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="CheckCircle"
                                onClick={() => handleVendorAction(vendor?.id, 'approve')}
                              />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="MoreHorizontal"
                              onClick={() => console.log('More actions for:', vendor?.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary">
                Vendor Analytics
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">Monthly Vendor Growth</h4>
                  <div className="space-y-3">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May']?.map((month, index) => (
                      <div key={month} className="flex items-center justify-between">
                        <span className="text-text-primary">{month} 2024</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${Math.random() * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">+{Math.floor(Math.random() * 50)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">Performance Metrics</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Average Rating</span>
                      <span className="font-medium text-text-primary">4.7</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Response Rate</span>
                      <span className="font-medium text-text-primary">89.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Completion Rate</span>
                      <span className="font-medium text-text-primary">96.8%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Revenue per Vendor</span>
                      <span className="font-medium text-text-primary">{formatPrice(2340)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorManagementPanel;